import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getMySite, updateSite, getAnalytics, logoutUser, getMe } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { TEMPLATES } from "@/lib/templates";
import {
  ExternalLink, Edit3, Save, X, BarChart3, Mail, LogOut,
  Phone, MapPin, Clock, Loader2, CheckCircle, Globe
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [site, setSite] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [message, setMessage] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [userRes, siteRes, analyticsRes] = await Promise.all([
        getMe(),
        getMySite(),
        getAnalytics().catch(() => ({ data: { total_visits: 0, total_unique_visitors: 0 } }))
      ]);
      setUser(userRes.data);
      setSite(siteRes.data.site);
      setAnalytics(analyticsRes.data);
    } catch {
      // If no site, that's ok
    } finally {
      setLoading(false);
    }
  };

  const startEdit = () => {
    setEditing(true);
    setEditForm({
      business_name: site?.business_name || "",
      phone: site?.phone || "",
      city: site?.city || "",
      state: site?.state || "",
      hours: site?.hours || "Mon-Fri 8am-5pm",
      services: site?.services || [],
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await updateSite(editForm);
      setSite(res.data.site);
      setEditing(false);
      setMessage("Changes saved!");
      setTimeout(() => setMessage(""), 3000);
    } catch {
      setMessage("Failed to save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logoutUser();
    navigate('/');
  };

  const template = TEMPLATES.find(t => t.id === site?.trade);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0F1F] flex items-center justify-center">
        <Loader2 className="animate-spin text-orange-500" size={32} />
      </div>
    );
  }

  // No site yet — redirect to signup
  if (!site) {
    return (
      <div className="min-h-screen bg-[#0A0F1F] flex items-center justify-center px-6" data-testid="no-site-dashboard">
        <div className="text-center max-w-md">
          <h1 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Welcome to SiteSmith
          </h1>
          <p className="text-slate-400 mb-8">You don't have a site yet. Let's build one in 2 minutes.</p>
          <Button
            data-testid="dashboard-create-site-btn"
            onClick={() => navigate('/signup')}
            className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 text-lg rounded-sm font-semibold"
          >
            Build My Site
          </Button>
          <button
            onClick={handleLogout}
            className="block mx-auto mt-4 text-slate-500 hover:text-slate-300 text-sm"
            data-testid="dashboard-logout-btn-nosit"
          >
            Log out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0F1F]" data-testid="dashboard-page">
      {/* Header */}
      <div className="border-b border-slate-800 bg-[#0A0F1F]/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <span className="text-white font-bold text-lg" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Site<span className="text-slate-400 font-semibold">Smith</span></span>
          <div className="flex items-center gap-4">
            <span className="text-slate-400 text-sm hidden sm:block">{user?.email}</span>
            <button
              onClick={handleLogout}
              className="text-slate-400 hover:text-white transition-colors"
              data-testid="dashboard-logout-btn"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {message && (
          <div className="bg-green-900/30 border border-green-700 text-green-400 px-4 py-3 rounded-sm mb-6 flex items-center gap-2" data-testid="dashboard-message">
            <CheckCircle size={18} /> {message}
          </div>
        )}

        {/* View My Site - Big CTA */}
        <div className="border border-slate-700 rounded-sm p-6 sm:p-8 bg-slate-800/30 mb-6" data-testid="dashboard-view-site-card">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "'Clash Display', 'Work Sans', sans-serif" }}>
                {site.business_name}
              </h1>
              <div className="flex items-center gap-2 mt-1">
                <Globe size={14} className="text-green-500" />
                <span className="text-green-400 text-sm">Live</span>
                <span className="text-slate-500 text-sm">&mdash; {site.subdomain}.sitesmith.com</span>
              </div>
            </div>
            <a
              href={`${BACKEND_URL}/api/sites/${site.site_id}/html`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-sm font-semibold flex items-center gap-2 transition-colors"
              data-testid="view-my-site-btn"
            >
              <ExternalLink size={18} /> View My Site
            </a>
          </div>
        </div>

        {/* Dashboard Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Business Info */}
          <div className="lg:col-span-2 border border-slate-700 rounded-sm p-6 bg-slate-800/30" data-testid="dashboard-info-card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-white">Business Info</h2>
              {!editing ? (
                <button
                  onClick={startEdit}
                  className="text-orange-500 hover:text-orange-400 flex items-center gap-1 text-sm"
                  data-testid="edit-info-btn"
                >
                  <Edit3 size={14} /> Edit
                </button>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="text-green-500 hover:text-green-400 flex items-center gap-1 text-sm"
                    data-testid="save-info-btn"
                  >
                    {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save
                  </button>
                  <button
                    onClick={() => setEditing(false)}
                    className="text-slate-400 hover:text-slate-300 flex items-center gap-1 text-sm"
                    data-testid="cancel-edit-btn"
                  >
                    <X size={14} /> Cancel
                  </button>
                </div>
              )}
            </div>

            {editing ? (
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-400 mb-1 block text-sm">Business Name</Label>
                  <Input
                    data-testid="edit-business-name"
                    value={editForm.business_name}
                    onChange={e => setEditForm(p => ({ ...p, business_name: e.target.value }))}
                    className="bg-slate-900 border-slate-700 text-white rounded-sm"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-400 mb-1 block text-sm">Phone</Label>
                    <Input
                      data-testid="edit-phone"
                      value={editForm.phone}
                      onChange={e => setEditForm(p => ({ ...p, phone: e.target.value }))}
                      className="bg-slate-900 border-slate-700 text-white rounded-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 mb-1 block text-sm">Hours</Label>
                    <Input
                      data-testid="edit-hours"
                      value={editForm.hours}
                      onChange={e => setEditForm(p => ({ ...p, hours: e.target.value }))}
                      className="bg-slate-900 border-slate-700 text-white rounded-sm"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-400 mb-1 block text-sm">City</Label>
                    <Input
                      data-testid="edit-city"
                      value={editForm.city}
                      onChange={e => setEditForm(p => ({ ...p, city: e.target.value }))}
                      className="bg-slate-900 border-slate-700 text-white rounded-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 mb-1 block text-sm">State</Label>
                    <Input
                      data-testid="edit-state"
                      value={editForm.state}
                      onChange={e => setEditForm(p => ({ ...p, state: e.target.value }))}
                      className="bg-slate-900 border-slate-700 text-white rounded-sm"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-slate-300">
                  <Phone size={16} className="text-slate-500 shrink-0" />
                  <span>{site.phone}</span>
                </div>
                <div className="flex items-center gap-3 text-slate-300">
                  <MapPin size={16} className="text-slate-500 shrink-0" />
                  <span>{site.city}, {site.state}</span>
                </div>
                <div className="flex items-center gap-3 text-slate-300">
                  <Clock size={16} className="text-slate-500 shrink-0" />
                  <span>{site.hours}</span>
                </div>
                <div className="mt-4">
                  <span className="text-sm text-slate-500 mb-2 block">Services</span>
                  <div className="flex flex-wrap gap-2">
                    {site.services?.map((s, i) => (
                      <span key={i} className="text-xs bg-slate-700 text-slate-300 px-3 py-1 rounded-sm">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right column */}
          <div className="space-y-6">
            {/* Analytics */}
            <div className="border border-slate-700 rounded-sm p-6 bg-slate-800/30" data-testid="dashboard-analytics-card">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 size={18} className="text-orange-500" />
                <h3 className="text-white font-medium">Site Visits</h3>
              </div>
              <div className="text-3xl font-bold text-white mb-1">
                {analytics?.total_visits || 0}
              </div>
              <p className="text-sm text-slate-500">Total visits this month</p>
              <div className="mt-3 text-sm text-slate-400">
                <span className="font-medium text-white">{analytics?.total_unique_visitors || 0}</span> unique visitors
              </div>
            </div>

            {/* Billing */}
            <div className="border border-slate-700 rounded-sm p-6 bg-slate-800/30" data-testid="dashboard-billing-card">
              <h3 className="text-white font-medium mb-3">Billing</h3>
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-2 h-2 rounded-full ${site.subscription_status === 'active' ? 'bg-green-500' : 'bg-yellow-500'}`} />
                <span className="text-sm text-slate-300 capitalize">{site.subscription_status}</span>
              </div>
              <p className="text-sm text-slate-500 mb-4">$99/month — website, hosting, security & DDoS protection</p>
              <button
                className="w-full border border-slate-600 text-slate-300 hover:bg-slate-700 py-2 rounded-sm text-sm transition-colors"
                data-testid="manage-billing-btn"
              >
                Manage Billing
              </button>
            </div>

            {/* Contact */}
            <div className="border border-white/[0.06] rounded-sm p-6 bg-slate-800/30" data-testid="dashboard-contact-card">
              <h3 className="text-white font-medium mb-3">Need help?</h3>
              <p className="text-sm text-slate-400 mb-4">Questions? We're here for you.</p>
              <a
                href="/get-started"
                className="flex items-center gap-2 text-orange-500 hover:text-orange-400 text-sm"
                data-testid="contact-support-link"
              >
                <Mail size={14} /> Contact Us
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
